import { isApiConfigured, type ApiKeyName } from './api-keys';
import { shouldUseLLMProxy } from '../lib/openai-proxy';

interface ValidationResult {
  valid: boolean;
  warnings: string[];
  errors: string[];
  availableFeatures: string[];
  missingFeatures: string[];
}

export function validateEnvironment(): ValidationResult {
  const result: ValidationResult = {
    valid: true,
    warnings: [],
    errors: [],
    availableFeatures: [],
    missingFeatures: [],
  };

  // Required in production: same-origin LLM proxy. Provider keys live in
  // Cloudflare secrets and are not visible to browser-side validation.
  if (!shouldUseLLMProxy() && import.meta.env.PROD) {
    result.errors.push('No server-side LLM proxy configured for this public build.');
    result.valid = false;
  } else if (shouldUseLLMProxy()) {
    result.availableFeatures.push('LLM providers (Cloudflare proxy)');
  }

  // Optional features
  const optionalFeatures: { key: ApiKeyName; feature: string }[] = [
    { key: 'googlePlaces', feature: 'Restaurant Agent (Google Places)' },
    { key: 'plaidClientId', feature: 'Financial data (Plaid)' },
    { key: 'serpapi', feature: 'Travel Agent (SerpAPI)' },
    { key: 'ticketmaster', feature: 'Events Agent (Ticketmaster)' },
    { key: 'newsApi', feature: 'Content Agent (News API) — Sprint 17' },
    { key: 'sentryDsn', feature: 'Error monitoring (Sentry)' },
  ];

  for (const { key, feature } of optionalFeatures) {
    if (isApiConfigured(key)) {
      result.availableFeatures.push(feature);
    } else {
      result.missingFeatures.push(feature);
      result.warnings.push(`${feature} disabled: ${key} not configured.`);
    }
  }

  return result;
}

export function logEnvironmentStatus(): void {
  const result = validateEnvironment();

  if (import.meta.env.DEV) {
    console.group('OwnYou Environment Status');
    if (result.errors.length > 0) {
      console.error('ERRORS:', result.errors);
    }
    if (result.warnings.length > 0) {
      console.warn('Warnings:', result.warnings);
    }
    if (result.availableFeatures.length > 0) {
      console.log('Available features:', result.availableFeatures);
    }
    if (result.missingFeatures.length > 0) {
      console.log('Missing features:', result.missingFeatures);
    }
    console.groupEnd();
  }
}
